/**
 * Mensagem de erro legível em português para Axios / fetch / Error.
 *
 * Regras:
 * - nunca mostra prefixo "(nnn)", statusText HTTP, `title` de problem-details do ASP.NET
 *   nem mensagens cruas do Axios ("timeout of…", "Request aborted", "Network Error");
 * - usa a mensagem do servidor só quando ela é texto para o usuário (em português);
 * - quando não há nada útil, devolve o `fallback` de quem chamou.
 */

export const SESSION_EXPIRED_MESSAGE = 'Sua sessão expirou. Entre de novo.';
export const FORBIDDEN_MESSAGE = 'Você não tem acesso a isso nesta conexão.';
export const NOT_FOUND_MESSAGE = 'Não encontramos o que você pediu. Pode ter sido removido.';
export const CONFLICT_MESSAGE = 'Isso mudou enquanto você mexia. Atualize a página e tente de novo.';
export const RATE_LIMIT_MESSAGE = 'Muitas tentativas seguidas. Espere um pouco e tente de novo.';
export const SERVER_ERROR_MESSAGE = 'O servidor teve um problema. Tente de novo em instantes.';
export const TIMEOUT_MESSAGE = 'O servidor demorou para responder. Tente de novo.';
export const NETWORK_MESSAGE = 'Falha de rede ao falar com o servidor. Confira sua conexão e tente de novo.';
export const ABORTED_MESSAGE = 'A requisição foi interrompida. Tente de novo.';

type ErrorLike = {
  message?: unknown;
  code?: unknown;
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
  };
};

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error == null) return fallback;

  if (typeof error === 'string') {
    const t = error.trim();
    if (!t) return fallback;
    return technicalMessage(t) || fallback;
  }

  if (typeof error !== 'object') return fallback;
  const err = error as ErrorLike;
  const status = typeof err.response?.status === 'number' ? err.response.status : undefined;
  const serverMessage = readApiBodyMessage(err.response?.data);

  if (status && status >= 400) {
    switch (true) {
      case status === 401:
        return SESSION_EXPIRED_MESSAGE;
      case status === 403:
        return FORBIDDEN_MESSAGE;
      case status === 404:
        return serverMessage ?? NOT_FOUND_MESSAGE;
      case status === 408:
        return TIMEOUT_MESSAGE;
      case status === 409:
        return serverMessage ?? CONFLICT_MESSAGE;
      case status === 429:
        return RATE_LIMIT_MESSAGE;
      case status >= 500:
        return serverMessage ?? SERVER_ERROR_MESSAGE;
      default:
        // 400, 422 e demais 4xx: motivo do servidor quando é texto para o usuário.
        return serverMessage ?? fallback;
    }
  }

  const code = typeof err.code === 'string' ? err.code : '';
  const message = typeof err.message === 'string' ? err.message.trim() : '';

  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return TIMEOUT_MESSAGE;
  if (code === 'ERR_CANCELED') return ABORTED_MESSAGE;
  if (code === 'ERR_NETWORK') return NETWORK_MESSAGE;

  if (message) {
    const technical = technicalMessage(message);
    if (technical !== null) return technical || fallback;
    // Erro lançado pelo próprio app (não é resposta HTTP nem erro do Axios): o texto foi escrito por nós para a tela.
    if (!err.response && !(err as { isAxiosError?: boolean }).isAxiosError && isUserFacing(message)) return message;
  }

  if (serverMessage) return serverMessage;
  return fallback;
}

/** Marca para "mensagem crua do Axios sem texto útil": quem chama usa o próprio fallback. */
const GENERIC_HTTP_FAILURE = '';

/** Mensagens cruas conhecidas do Axios / navegador traduzidas para português. */
function technicalMessage(message: string): string | null {
  const lower = message.toLowerCase();
  if (lower.startsWith('timeout of') || lower.includes('timed out')) return TIMEOUT_MESSAGE;
  if (lower.startsWith('request failed with status code')) return GENERIC_HTTP_FAILURE;
  if (lower === 'request aborted' || lower === 'canceled' || lower === 'cancelled' || lower.includes('aborted')) {
    return ABORTED_MESSAGE;
  }
  if (lower === 'network error' || lower === 'failed to fetch' || lower.includes('networkerror')) {
    return NETWORK_MESSAGE;
  }
  return null;
}

/**
 * Mensagem do corpo da resposta, decidida pela FORMA da resposta e não pelo idioma do texto:
 * - corpo no envelope da API da MENDS (objeto com `message`/`error`/`detail`) → é texto escrito para o usuário;
 * - problem-details do ASP.NET (`title`, `traceId`, `type`, `errors` de validação) → nunca é mostrado;
 * - texto solto ou HTML → nunca é mostrado (pode ser página de proxy ou stack trace).
 * O que não serve cai no fallback de quem chamou.
 */
function readApiBodyMessage(data: unknown): string | null {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return null;
  const obj = data as Record<string, unknown>;
  const isProblemDetails = ['title', 'traceId', 'type', 'errors', 'Title', 'TraceId', 'Errors'].some((k) => k in obj);
  if (isProblemDetails) return null;
  for (const candidate of [obj.message, obj.Message, obj.error, obj.Error, obj.detail, obj.Detail]) {
    const s = pickStr(candidate);
    if (s && isUserFacing(s)) return truncate(s);
  }
  return null;
}

// Marcas de formato de exceção/stack trace (estrutura, não idioma): nunca vão para a tela.
const TECH_MARKERS = ['exception', 'system.', 'microsoft.', 'npgsql', 'stack trace', '   at ', 'traceid', 'object reference'];

/** Texto da API para o usuário: sem formato de exceção e de tamanho razoável. */
function isUserFacing(text: string): boolean {
  const lower = text.toLowerCase();
  return !TECH_MARKERS.some((m) => lower.includes(m)) && text.length <= 600;
}

function truncate(t: string): string {
  return t.length > 400 ? `${t.slice(0, 400)}…` : t;
}

function pickStr(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}
